<template>
  <v-layout column>
    <review-signature-request-dialog
      :opened.sync="reviewSignatureRequestDialogOpened"
      :currentCompany="currentCompany"
      :signatureRequest="currentSignatureRequest"
      @refresh-table="refreshTable"
      @refresh-table-until-sent="refreshTableUntilSent"
      @download-document="downloadDocument"
      @download-signed-pages-only="downloadSignedPagesOnly"
    />

    <signature-request-in-process-dialog
      :opened.sync="signatureRequestInProcessDialogOpened"
      @cancelProcessing="cancelProcessing"
    />

    <v-layout column>
      <div class="text-xs-left">Search</div>

      <v-text-field
        class="default-input subheading mb-2"
        label="Enter to search"
        v-model="signatureSearch"
        single-line
        solo
        hide-details
        append-icon="search"
      />
    </v-layout>

    <v-data-table
      :headers="headers"
      :items="allSignatureRequests"
      :search="signatureSearch"
      :custom-filter="customSignatureFilter"
      :pagination.sync="pagination"
      :class="
        $vuetify.breakpoint.mdAndUp
          ? 'overview-data-table'
          : 'mobile-overview-data-table'
      "
      :loading="tableLoading"
    >
      <template v-slot:items="props">
        <tr class="activity-row">
          <td class="text-xs-left">
            <v-layout
              @click="
                changeCurrentCompany(
                  $event,
                  props.item.company_id,
                  `/admin-dashboard/${
                    findCompany(props.item.company_id).hash_id
                  }`,
                )
              "
              :href="
                '/admin-dashboard/' + findCompany(props.item.company_id).hash_id
              "
              style="cursor: pointer"
            >
              {{ props.item.company_name }}
            </v-layout>
          </td>

          <td class="text-xs-left">
            <v-layout
              @click="viewSignatureRequest(props.item)"
              style="cursor: pointer"
            >
              <span>{{ getName(props.item) }}</span>
            </v-layout>
          </td>

          <td class="text-xs-center">
            <v-layout align-center>
              <v-progress-circular
                v-if="props.item.status === 'processing'"
                indeterminate
                color="primary"
                size="24"
                class="mr-2"
              />
              <i
                v-else
                class="material-icons-outlined mr-2"
                :class="getSignatureStatus(props.item).class"
              >
                {{ getSignatureStatus(props.item).icon }}</i
              >
              <span class="font-weight-bold">
                {{ getSignatureStatus(props.item).text }}
              </span>
            </v-layout>
          </td>

          <td class="text-xs-left">
            <span>{{ moment(props.item.updated_at).fromNow() }}</span>
          </td>

          <td>
            <v-layout align-center justify-center>
              <v-menu
                transition="slide-y-transition"
                bottom
                left
                nudge-bottom="30"
                class="my-1"
              >
                <v-icon slot="activator" color="#0a1a25"> more_vert </v-icon>

                <v-list>
                  <v-list-tile @click="downloadDocument(props.item)">
                    <v-list-tile-title>
                      Download (all pages)
                    </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile
                    v-if="props.item.signed_pages_only !== null"
                    @click="downloadSignedPagesOnly(props.item)"
                  >
                    <v-list-tile-title>
                      Download (signed pages only)
                    </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile
                    v-if="props.item.status === 'sent'"
                    @click="resend(props.item)"
                  >
                    <v-list-tile-title> Resend </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile
                    v-if="props.item.status !== 'completed'"
                    @click="markAsCompleted(props.item)"
                  >
                    <v-list-tile-title> Mark as completed </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile @click="remove(props.item)">
                    <v-list-tile-title> Remove </v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import SignatureRequestsMixin from '@/mixins/SignatureRequestsMixin';
import ReviewSignatureRequestDialog from '@/components/dialogs/ReviewSignatureRequestDialog';
import SignatureRequestInProcessDialog from '@/components/dialogs/SignatureRequestInProcessDialog';

export default {
  components: {
    ReviewSignatureRequestDialog,
    SignatureRequestInProcessDialog,
  },

  props: {
    allSignatureRequests: {
      type: Array,
    },

    tableLoading: {
      type: Boolean,
    },
  },

  mixins: [SignatureRequestsMixin],

  data() {
    return {
      signatureSearch: '',

      pagination: {
        descending: true,
        rowsPerPage: 25,
      },

      selectedRequest: null,
      headers: [
        {
          text: 'Entity (Click for detail)',
          align: 'left',
          width: '20%',
          value: 'company_name',
        },
        {
          text: 'Document Name',
          align: 'left',
          width: '30%',
          value: 'source',
        },
        {
          text: 'Status',
          align: 'left',
          width: '15%',
          value: 'status',
        },
        {
          text: 'Last Activity',
          align: 'left',
          width: '15%',
          value: 'updated_at',
        },
        {
          text: 'Actions',
          align: 'center',
          width: '5%',
        },
      ],

      signatureStatus: 'All',

      reviewSignatureRequestDialogOpened: false,
      signatureRequestInProcessDialogOpened: false,

      currentSignatureRequest: null,
      currentCompany: null,
    };
  },

  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
    }),
  },

  methods: {
    moment,
    isEmpty,

    findCompany(company_id) {
      return this.companies.find(c => c.id === company_id);
    },

    customSignatureFilter(items, search) {
      const searchedItem = search.toLowerCase();

      return items.filter(row => {
        const source = row.source;
        const companyName = row.company_name;

        let rowExists = true;

        if (searchedItem.length) {
          rowExists =
            row.form_name.toLowerCase().includes(searchedItem) ||
            row.company_name.toLowerCase().includes(searchedItem);
        }

        if (this.signatureStatus !== 'All' && rowExists) {
          rowExists = row.status.toLowerCase() === this.signatureStatus;
        }
        return rowExists;
      });
    },

    viewSignatureRequest(item) {
      if (item.status === 'processing') {
        this.currentSignatureRequest = item;
        this.signatureRequestInProcessDialogOpened = true;
      } else {
        this.currentSignatureRequest = item;
        this.currentCompany = this.findCompany(item.company_id);
        this.reviewSignatureRequestDialogOpened = true;
      }
    },

    async changeCurrentCompany(event, companyId, path) {
      event && event.preventDefault && event.preventDefault();
      await this.$store.dispatch('resetTiles', { companyId });

      this.$router.push(path);
    },

    refreshTable() {
      this.$emit('refresh-table');
    },

    refreshTableUntilSent(signatureRequestId) {
      this.$emit('refresh-table-until-sent', signatureRequestId);
    },

    async cancelProcessing() {
      try {
        this.tableLoading = true;
        this.signatureRequestInProcessDialogOpened = false;

        await API.signatureRequest.cancelProcessing({
          id: this.currentSignatureRequest.id,
          companyId: this.currentSignatureRequest.company_id,
        });

        await this.refreshTable();
      } finally {
        this.tableLoading = false;
      }
    },
  },
};
</script>

<style lang="postcss" scoped></style>
